<script>
import Draggable from 'vuedraggable';
import { mapGetters } from 'vuex';
import getUuid from 'widget/helpers/uuid';
import { setStepMoveInProgress } from 'dashboard/store/utils/wgptAutomationBuilderUtils';
import {
  computeStepVariablesMap,
  insertStepAt,
} from '../../../helpers/automationUtils';
import AddStepButton from '../../addStepDropdown/AddStepButton.vue';

export default {
  components: {
    AddStepButton,
    Draggable,
  },
  props: {
    branch: {
      type: Object,
      default: () => {},
    },
    index: {
      type: Number,
      default: -1,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    variables: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['stepChange', 'stepDelete'],
  computed: {
    ...mapGetters({
      actionsRegistry: 'wgptAutomationRegistry/getActions',
    }),
    steps() {
      return this.branch.steps || [];
    },
    branchName() {
      if (this.branch.name) return this.branch.name;
      if (this.index === -1)
        return this.$t('WGPT_AUTOMATIONS.EDITOR.BRANCH.DEFAULT_BRANCH_NAME');
      return this.$t('WGPT_AUTOMATIONS.EDITOR.BRANCH.FALLBACK_BRANCH_NAME', {
        index: this.index + 1,
      });
    },
    isEmpty() {
      return this.steps.length === 0;
    },
    draggableStep: {
      get() {
        return [];
      },
      set(steps) {
        const selectedStepId = this.$route.query.selected;
        let newSelectedId = null;

        steps.forEach(step => {
          const newId = getUuid();
          if (step.id === selectedStepId) newSelectedId = newId;
          this.addStepAt(0, { ...step, id: newId });
        });

        if (newSelectedId) {
          this.$router
            .replace({ query: { selected: newSelectedId } })
            .catch(() => {});
        }
        setStepMoveInProgress(false);
      },
    },
    stepVariablesMap() {
      return computeStepVariablesMap(
        this.steps,
        this.actionsRegistry,
        this.variables
      );
    },
  },
  methods: {
    onStepDelete(...step) {
      this.$emit('stepDelete', ...step);
    },
    addStepAt(index, newStep) {
      const branch = {
        ...this.branch,
        steps: insertStepAt(this.steps, index, newStep),
      };
      this.$emit('stepChange', this.branch.id, branch);
    },
  },
};
</script>

<template>
  <div class="branch">
    <div class="connector-bridge" />

    <div class="connector-top">
      <div class="branch-label !mx-0 !mt-4 pointer-events-auto">
        <woot-label
          :title="branchName"
          color-scheme="primary"
          class="max-w-[200px] cursor-default !m-0"
        />
      </div>
      <AddStepButton
        class="mt-4 m-0"
        :class="{
          'mb-4': !isEmpty,
          invisible: disabled,
          hidden: disabled && isEmpty,
        }"
        @add-step="e => addStepAt(0, e)"
        @delete-step="onStepDelete"
      />
    </div>

    <div class="steps">
      <slot
        v-if="steps.length"
        name="step"
        :step-variables-map="stepVariablesMap"
      />

      <Draggable
        v-else
        v-model="draggableStep"
        class="branch-draggable"
        :empty-insert-threshold="24"
        item-key="id"
        group="steps"
      >
        <template #item="{ element }">
          <div class="fake">{{ element }}</div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.branch-draggable {
  @apply relative;
}

.branch {
  @apply w-max min-w-[216px];
}

.connector-top {
  @apply flex flex-col items-end w-1/2 pointer-events-none rounded-none border-dashed border-slate-400 dark:border-slate-600;
  --transform: translateX(50%);
}

.connector-bridge {
  @apply w-full border-t-[1px] border-dashed border-slate-400 dark:border-slate-600;
}

.branch-label {
  transform: var(--transform);
}
</style>
